import { Button, Card, CardContent, cn, Skeleton } from '@up2stay/up2promo-ui';

import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';
import type { DashboardTopBusinessCardRow } from './dashboard-status-types';

export type DashboardTopCardRow = DashboardTopBusinessCardRow;

export function DashboardTopBusinessCardsCard({
  rows,
  isLoading,
  onOpenTeam,
  labels,
}: {
  rows: DashboardTopBusinessCardRow[];
  isLoading: boolean;
  onOpenTeam: () => void;
  labels: {
    title: string;
    team: string;
    empty: string;
  };
}) {
  if (isLoading) {
    return (
      <Card className={cn(statusBoardCardClass, 'h-full')} aria-busy="true">
        <CardContent className={cn('space-y-3', statusBoardCardPadClass)}>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <section
      aria-labelledby="dashboard-top-cards-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn('flex h-full flex-col gap-3', statusBoardCardPadClass)}
        >
          <div className="flex items-center gap-2">
            <h2
              id="dashboard-top-cards-heading"
              className={cn('min-w-0 flex-1', statusBoardSectionTitleClass)}
            >
              {labels.title}
            </h2>
            <Button
              type="button"
              variant="ghostAccent"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={onOpenTeam}
            >
              {labels.team}
            </Button>
          </div>

          {rows.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              {labels.empty}
            </p>
          ) : (
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center gap-3 border-t border-border/60 py-2.5 first:border-t-0"
                >
                  {row.avatarUrl ? (
                    <img
                      src={row.avatarUrl}
                      alt=""
                      className="size-[38px] shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className={cn(
                        'flex size-[38px] shrink-0 items-center justify-center rounded-full text-[13px] font-semibold',
                        row.avatarTone === 'warm'
                          ? 'bg-[#e3b98f] text-[#3a2412]'
                          : 'bg-[#c9d6fa] text-[#1c3fb8]',
                      )}
                    >
                      {row.initials}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-medium text-foreground">
                      {row.name}
                    </span>
                    {row.meta ? (
                      <span className="block truncate text-[12px] text-muted-foreground">
                        {row.meta}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-mono text-[13px] tabular-nums text-muted-foreground">
                    {row.tapsLabel}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
